/**
 * LocaleProvider — top-level locale context for the SPA.
 *
 * Responsibilities:
 *   1. Read the current locale from the URL (first path segment).
 *   2. Fall back to cookie preference, then browser Accept-Language,
 *      then the default locale — but never AUTO-REDIRECT the user
 *      (per Google's international-SEO guidance: auto-redirects
 *      confuse the crawler and get sites flagged as cloaking).
 *   3. Expose useLocale() hook so components can:
 *        - get the current locale metadata (name, region, currency)
 *        - construct locale-aware paths
 *        - trigger a locale change (updates URL + cookie)
 *   4. Set <html lang> and <html dir> attributes automatically so
 *      screen readers, browsers, and SEO auditors see the right
 *      language declaration.
 */

import { createContext, useContext, useEffect, useMemo, useCallback, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { DEFAULT_LOCALE, LOCALES, getLocale, localeFromPath, stripLocalePrefix, type Locale } from './locales';

interface LocaleContextValue {
  locale: Locale;
  locales: Locale[];
  changeLocale: (code: string) => void;
  localizedPath: (path: string, targetLocale?: string) => string;
}

const LocaleContext = createContext<LocaleContextValue | undefined>(undefined);

const COOKIE_NAME = 'lbt_locale';
const COOKIE_MAX_AGE = 60 * 60 * 24 * 365; // 1 year

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') return null;
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string) {
  if (typeof document === 'undefined') return;
  /* SameSite=Lax so it survives cross-site GET navigation (needed when
     users arrive from Google search) but blocks CSRF. Path=/ so every
     route sees it. Secure only in prod (localhost dev over HTTP still
     needs to read/write). */
  const secure = typeof window !== 'undefined' && window.location.protocol === 'https:' ? '; Secure' : '';
  document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=${COOKIE_MAX_AGE}; SameSite=Lax${secure}`;
}

function inferBrowserLocale(): string {
  if (typeof navigator === 'undefined') return DEFAULT_LOCALE.code;
  /* Try `navigator.languages` (ordered by preference) then `navigator.language`.
     Match against our supported locales — first prefer exact match, then
     language-only match (e.g. `es-AR` → `es-es`). */
  const candidates = (navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language]).map(c => c.toLowerCase());
  for (const c of candidates) {
    if (getLocale(c)) return c;
    /* Language-only fallback */
    const lang = c.split('-')[0];
    const match = LOCALES.find(l => l.code.startsWith(lang + '-'));
    if (match) return match.code;
  }
  return DEFAULT_LOCALE.code;
}

export function LocaleProvider({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();

  /* Read locale from URL first (source of truth for the current view).
     Cookie + browser Accept-Language only inform where to send the user
     if they're on the default locale but their preference is elsewhere —
     even then we DON'T auto-redirect; we just make the language switcher
     highlight the recommended locale. */
  const urlLocaleCode = localeFromPath(location.pathname);
  const locale = getLocale(urlLocaleCode) || DEFAULT_LOCALE;

  /* Update <html lang> + <html dir> whenever locale changes. */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.lang = locale.hreflang.toLowerCase();
    document.documentElement.dir = locale.dir;
  }, [locale]);

  /* Persist the URL-derived locale as the user's preference. Only writes
     when it actually changes so we don't spam Set-Cookie on every nav. */
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const current = readCookie(COOKIE_NAME);
    if (current !== locale.code) writeCookie(COOKIE_NAME, locale.code);
  }, [locale.code]);

  const changeLocale = useCallback((code: string) => {
    const target = getLocale(code);
    if (!target) return;
    const canonicalPath = stripLocalePrefix(location.pathname);
    const newPath = target.code === DEFAULT_LOCALE.code
      ? canonicalPath || '/'
      : `/${target.code}${canonicalPath === '/' ? '' : canonicalPath}`;
    writeCookie(COOKIE_NAME, target.code);
    navigate(newPath + location.search + location.hash);
  }, [location, navigate]);

  const localizedPathFn = useCallback((path: string, targetLocale?: string): string => {
    const code = targetLocale || locale.code;
    if (code === DEFAULT_LOCALE.code) return path.startsWith('/') ? path : `/${path}`;
    const clean = path.startsWith('/') ? path : `/${path}`;
    return `/${code}${clean === '/' ? '' : clean}`;
  }, [locale.code]);

  const value = useMemo<LocaleContextValue>(() => ({
    locale,
    locales: LOCALES,
    changeLocale,
    localizedPath: localizedPathFn,
  }), [locale, changeLocale, localizedPathFn]);

  return <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>;
}

export function useLocale(): LocaleContextValue {
  const ctx = useContext(LocaleContext);
  if (!ctx) throw new Error('useLocale must be used within a LocaleProvider');
  return ctx;
}

/* Hint for first-visit: read cookie + browser to decide whether to
   suggest a different locale via a dismissible banner (not auto-redirect).
   Called from the banner component. Returns null if the user is already
   on their preferred locale. */
export function getSuggestedLocale(currentCode: string): string | null {
  if (typeof window === 'undefined') return null;
  const cookieLocale = readCookie(COOKIE_NAME);
  if (cookieLocale && cookieLocale !== currentCode) return cookieLocale;
  const browserLocale = inferBrowserLocale();
  if (browserLocale !== currentCode) return browserLocale;
  return null;
}
